import type { User } from '@paintscout/api'
import { AppDocsDocument, SearchUsersDocument, useDeactivateUserMutation } from '@paintscout/api'
import type { DialogProps, DropdownSelectOption } from '@paintscout/ui'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  RadioButtons,
  useClientOptions,
  useDialogs,
  EstimatorSelect,
  IconButton,
  Collapse
} from '@paintscout/ui'
import { formatName, getObjectLabels } from '@paintscout/util/builder'
import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { getOperationName } from 'apollo-link'
import * as Sentry from '@sentry/react'
import ClearIcon from '@material-ui/icons/Clear'

export interface DeactivateUserDialogProps extends DialogProps {
  user: User
  onConfirm: () => void
}

export function ManageEstimatesDialog({ user, onConfirm, ...props }: DeactivateUserDialogProps) {
  const { options, clientId } = useClientOptions()
  const objectLabels = getObjectLabels({ options })
  const [transferTo, setTransferTo] = useState<string>('')
  const [transferType, setTransferType] = useState<string>('')
  const { dismissDialog, dismissAllDialogs } = useDialogs()
  const { enqueueSnackbar } = useSnackbar()
  const [deactivateUser, { loading }] = useDeactivateUserMutation()

  const quotesLabel = objectLabels.quote.plural.toLowerCase()

  return (
    <Dialog {...props}>
      <DialogTitle
        loading={loading}
        rightContent={
          <IconButton style={{ padding: 0, marginBottom: 2 }} onClick={dismissAllDialogs}>
            <ClearIcon />
          </IconButton>
        }
      >
        <Typography variant="h2">Manage Existing {objectLabels.quote.plural}</Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body1">
              What would you like to do with <strong>{formatName(user.user_metadata)}'s</strong> existing {quotesLabel}?
            </Typography>
            <RadioButtons
              value={transferType}
              options={[
                {
                  label: 'Do Nothing',
                  name: 'none'
                },
                {
                  label: `Remove Estimator`,
                  name: 'remove'
                },
                { label: `Transfer ${quotesLabel} to another user`, name: 'transfer' }
              ]}
              onChange={setTransferType}
            />
          </Grid>
          <Grid item xs={12}>
            <Collapse show={transferType === 'transfer'}>
              <Grid item xs={12} style={{ marginBottom: 16 }}>
                <EstimatorSelect
                  variant="single"
                  name="estimator"
                  label={`Transfer ${quotesLabel} to`}
                  omit={[user.user_id]}
                  value={transferTo}
                  onChange={(option: DropdownSelectOption) => setTransferTo(option.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="warning">
                  <Typography style={{ marginBottom: 24 }}>
                    Transferred {quotesLabel} will count towards the selected user's dashboard metrics, and they'll
                    receive all related notifications.
                  </Typography>
                  <Typography weight={700}>This action is permanent and cannot be undone.</Typography>
                </Alert>
              </Grid>
            </Collapse>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        leftButton={
          <Button onClick={dismissDialog} variant="textNoPadding">
            Back
          </Button>
        }
      >
        <Button
          color="primary"
          variant="contained"
          disabled={!transferType || (transferType === 'transfer' && !transferTo) || loading}
          onClick={async () => {
            try {
              await deactivateUser({
                variables: {
                  id: user.user_id,
                  companyId: clientId,
                  transferType,
                  transferQuotesTo: transferTo
                },
                refetchQueries: [getOperationName(SearchUsersDocument), getOperationName(AppDocsDocument)]
              })
              enqueueSnackbar('User deactivated', { variant: 'success' })
              onConfirm()
            } catch (error) {
              enqueueSnackbar('Unable to deactivate user', { variant: 'error' })
              Sentry.captureException(error, { tags: { userId: user.user_id } })
              dismissDialog()
            }
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )
}
